import { useEffect, useMemo, useRef, useState } from "react";
import { useEscapeKey } from "../../../hooks/useEscapeKey";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

// ─── types ────────────────────────────────────────────────────────────────────

export type AsignacionFormType = "CLIENTE" | "CUENTA" | "RECLUTADOR";

export interface AsignacionItem {
  code: string;
  formType: string;
  personId?: string;
  DNI?: string;
  names?: string;
  clientId?: string;
  client?: string;
  businessArea?: string;
  /** Código del área (areaNegocioCod) — preferido para enviar al endpoint de asignación.
   *  Cuando no está, `businessArea` puede ser usado como fallback. */
  businessAreaCode?: string;
  vacancy?: string;
  recruiter?: string;
  /** ID del reclutador asociado a la postulación (presente en formularios tipo RECLUTADOR). */
  recruiterId?: string;
  /**
   * Estado calculado de la postulación (DISPONIBLE, INHABILITADO_CLIENTE, …).
   * Decide si el registro se debe reasignar obligatoriamente a otro cliente.
   */
  computedStatus?: string;
}

export interface AsignacionConfig {
  businessAreaCode?: string;
  vacancyId?: string;
  recruiterId?: string;
  /** Cliente de destino cuando se reasigna a un cliente distinto al original. */
  targetClientId?: string;
  /** Nombre del cliente de destino (para traza y para mostrarlo en el resumen). */
  targetClientName?: string;
}

interface IBusinessAreaOption { businessAreaId: string; code: string; }
interface IVacancyOption { vacancyId: string; jobName?: string; }
interface IRecruiterOption { userId: string; name: string; }
interface IClientOption { clientId: string; name: string; }

export interface AsignacionResultItem {
  personId: string;
  name?: string;
  DNI?: string;
  assigned: boolean;
  reason?: string;
  /** true cuando se asignó (o intentó) a un cliente distinto al original. */
  crossClient?: boolean;
  originClient?: string;
  targetClient?: string;
}

interface IPersonBlocks {
  globalBlock: boolean;
  blockedClientIds: string[];
  blockedVacancyIds: string[];
}

const INHABILITADO_STATUSES = new Set([
  "INHABILITADO_CLIENTE",
  "INHABILITADO_PROCESO",
]);

/** ¿El registro está inhabilitado para su cliente/proceso original? */
const isInhabItem = (item: AsignacionItem): boolean =>
  INHABILITADO_STATUSES.has(String(item.computedStatus ?? "").toUpperCase());

export interface AsignacionSummary {
  totalAssigned: number;
  totalRequested: number;
  results: AsignacionResultItem[];
}

type BusyScope = { kind: "item"; code: string } | { kind: "all" } | null;

interface IModalAsignarPostulantesProps {
  open: boolean;
  items: AsignacionItem[];
  onClose: () => void;
  onAssign?: (
    payload: { item: AsignacionItem; config: AsignacionConfig }[],
  ) => Promise<AsignacionSummary | undefined>;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

const groupBy = <T, K extends string>(arr: T[], key: (item: T) => K): Record<K, T[]> => {
  const out = {} as Record<K, T[]>;
  for (const item of arr) {
    const k = key(item);
    if (!out[k]) out[k] = [];
    out[k].push(item);
  }
  return out;
};

const formatGroupKey = (...parts: (string | undefined)[]): string =>
  parts.map((p) => (p ?? "").trim()).join("|");

const apiBase = (): string => process.env.REACT_APP_API_APPLICANT ?? "";

const fetchJson = async <T,>(url: string): Promise<T | null> => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
};

// ─── shared UI bits ───────────────────────────────────────────────────────────

const SectionHeader: React.FC<{ title: string; subtitle?: string; count: number; color: string; label: string }> = ({
  title, subtitle, count, color, label,
}) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "12px",
    padding: "14px 18px",
    background: `linear-gradient(135deg, ${color}22 0%, ${color}0d 100%)`,
    borderBottom: `1px solid ${color}33`,
  }}>
    <span style={{
      padding: "3px 10px", borderRadius: "6px",
      background: color, color: "#fff",
      fontSize: "10px", fontWeight: 700,
      fontFamily: "Roboto, sans-serif", letterSpacing: "0.08em",
      textTransform: "uppercase", flexShrink: 0,
    }}>
      {label}
    </span>
    <div style={{ flex: 1 }}>
      <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>{title}</p>
      {subtitle && (
        <p style={{ margin: "1px 0 0", fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#64748b" }}>{subtitle}</p>
      )}
    </div>
    <span style={{
      minWidth: "26px", height: "26px",
      display: "flex", alignItems: "center", justifyContent: "center",
      borderRadius: "50%", background: color, color: "#fff",
      fontSize: "13px", fontWeight: 700, fontFamily: "Roboto, sans-serif",
    }}>
      {count}
    </span>
  </div>
);

interface MiniSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: { code: string; name: string }[];
  placeholder: string;
  width?: number | string;
  disabled?: boolean;
}

const MiniSelect: React.FC<MiniSelectProps> = ({ value, onChange, options, placeholder, width = 160, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [panelPos, setPanelPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Esc con la lista abierta solo la cierra (en captura y marcando el evento,
  // para que no cierre también el modal; ver useEscapeKey).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setOpen(false);
      setSearch("");
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 190) });
    }
    setOpen((prev) => !prev);
  };

  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find((o) => o.code === value);

  const panel = open ? createPortal(
    <div
      ref={panelRef}
      style={{
        position: "fixed",
        top: panelPos.top,
        left: panelPos.left,
        width: panelPos.width,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        boxShadow: "0 8px 24px rgba(15,23,42,0.13)",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
        <input
          autoFocus
          placeholder="Buscar"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "5px 8px",
            border: "1px solid #e2e8f0", borderRadius: "6px",
            fontFamily: "Roboto, sans-serif", fontSize: "12px",
            outline: "none", color: "#334155", boxSizing: "border-box",
          }}
        />
      </div>
      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "10px 12px", fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "#94a3b8", textAlign: "center" }}>
            Sin resultados
          </div>
        ) : (
          filtered.map((o) => {
            const isSelected = o.code === value;
            return (
              <div
                key={o.code}
                onClick={() => { onChange(o.code); setOpen(false); setSearch(""); }}
                onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = isSelected ? "#eff6ff" : "transparent"; }}
                style={{
                  display: "flex", alignItems: "center", gap: "8px",
                  padding: "7px 12px", cursor: "pointer",
                  background: isSelected ? "#eff6ff" : "transparent",
                  fontFamily: "Roboto, sans-serif", fontSize: "12.5px",
                  color: isSelected ? "#0065DD" : "#334155",
                  fontWeight: isSelected ? 600 : 400,
                  transition: "background 0.1s", whiteSpace: "nowrap",
                }}
              >
                {isSelected
                  ? <RadioButtonCheckedIcon style={{ fontSize: 15, color: "#0065DD", flexShrink: 0 }} />
                  : <RadioButtonUncheckedIcon style={{ fontSize: 15, color: "#cbd5e1", flexShrink: 0 }} />
                }
                {o.name}
              </div>
            );
          })
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ position: "relative", width, flexShrink: 0 }}>
      <div
        ref={triggerRef}
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "6px 10px", borderRadius: "8px",
          border: `1px solid ${open ? "#0065DD" : "#cbd5e1"}`,
          background: disabled ? "#f1f5f9" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          fontFamily: "Roboto, sans-serif", fontSize: "12.5px",
          color: disabled ? "#94a3b8" : selected ? "#1e293b" : "#94a3b8",
          boxShadow: open ? "0 0 0 3px rgba(0,101,221,0.1)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          userSelect: "none", gap: "4px",
        }}
      >
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.name : placeholder}
        </span>
        <KeyboardArrowDownIcon
          style={{
            fontSize: 17,
            color: disabled ? "#cbd5e1" : "#64748b",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s", flexShrink: 0,
          }}
        />
      </div>
      {panel}
    </div>
  );
};

const Spinner: React.FC<{ size?: number; color?: string }> = ({ size = 14, color = "#fff" }) => (
  <span
    aria-label="cargando"
    style={{
      display: "inline-block",
      width: `${size}px`,
      height: `${size}px`,
      border: `2px solid ${color}55`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "asignar-spin 0.7s linear infinite",
    }}
  />
);

const RowActionButton: React.FC<{ label: string; onClick: () => void; disabled?: boolean; loading?: boolean }> = ({
  label, onClick, disabled, loading,
}) => {
  const isInactive = disabled || loading;
  return (
    <motion.button
      className={isInactive ? undefined : "asignar-btn"}
      onClick={onClick}
      disabled={isInactive}
      whileHover={{ scale: isInactive ? 1 : 1.03 }}
      whileTap={{ scale: isInactive ? 1 : 0.97 }}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
        padding: "6px 14px",
        borderRadius: "8px",
        border: isInactive ? "1px solid #cbd5e1" : "1px solid #1d4ed8",
        background: isInactive ? "#f1f5f9" : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
        color: isInactive ? "#94a3b8" : "#fff",
        fontFamily: "Roboto, sans-serif",
        fontSize: "12px",
        fontWeight: 600,
        cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        minWidth: "82px",
      }}
    >
      {loading ? <Spinner size={12} color={isInactive ? "#94a3b8" : "#fff"} /> : null}
      {loading ? "Asignando…" : label}
    </motion.button>
  );
};

const ApplyAllToggle: React.FC<{ active: boolean; onToggle: () => void }> = ({ active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    title="Cuando está activo, el reclutador/área/proceso del grupo se aplica a todos los registros del grupo"
    style={{
      display: "flex", alignItems: "center", gap: "8px",
      padding: "6px 10px",
      borderRadius: "999px",
      border: `1px solid ${active ? "#1d4ed8" : "#cbd5e1"}`,
      background: active ? "#dbeafe" : "#f8fafc",
      color: active ? "#1d4ed8" : "#475569",
      cursor: "pointer",
      fontFamily: "Roboto, sans-serif",
      fontSize: "12px",
      fontWeight: 600,
    }}
  >
    <span
      aria-hidden
      style={{
        position: "relative",
        display: "inline-block",
        width: "28px",
        height: "16px",
        borderRadius: "999px",
        background: active ? "#1d4ed8" : "#cbd5e1",
        transition: "background 0.15s ease",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: "2px",
          left: active ? "14px" : "2px",
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.15s ease",
        }}
      />
    </span>
    Aplicar a todos
  </button>
);

// Toggle compacto para habilitar la asignación de un registro Disponible a un
// cliente distinto al que postuló.
const CrossClientToggle: React.FC<{ active: boolean; onToggle: () => void }> = ({ active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    title="Asignar este postulante a un cliente distinto al que postuló"
    style={{
      display: "flex", alignItems: "center", gap: "6px",
      padding: "3px 9px",
      borderRadius: "999px",
      border: `1px solid ${active ? "#b45309" : "#cbd5e1"}`,
      background: active ? "#fff7ed" : "#f8fafc",
      color: active ? "#b45309" : "#64748b",
      cursor: "pointer",
      fontFamily: "Roboto, sans-serif",
      fontSize: "10.5px",
      fontWeight: 700,
    }}
  >
    <span
      aria-hidden
      style={{
        position: "relative", display: "inline-block",
        width: "24px", height: "14px", borderRadius: "999px",
        background: active ? "#f59e0b" : "#cbd5e1", transition: "background 0.15s ease",
      }}
    >
      <span
        style={{
          position: "absolute", top: "2px", left: active ? "12px" : "2px",
          width: "10px", height: "10px", borderRadius: "50%",
          background: "#fff", transition: "left 0.15s ease",
        }}
      />
    </span>
    Otro cliente
  </button>
);

const SubSeparator: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0 2px", margin: "4px 0 2px" }}>
    <div style={{ flex: 1, height: "1px", background: `${color}30` }} />
    <span style={{
      fontFamily: "Roboto, sans-serif", fontSize: "10px", fontWeight: 700,
      color, letterSpacing: "0.1em", textTransform: "uppercase",
      padding: "2px 8px", borderRadius: "4px", background: `${color}12`,
      border: `1px solid ${color}25`, whiteSpace: "nowrap",
    }}>
      {label}
    </span>
    <div style={{ flex: 1, height: "1px", background: `${color}30` }} />
  </div>
);

// Highlight on hover via inline style + state
const HoverableRow: React.FC<{ borderColor: string; children: React.ReactNode }> = ({ borderColor, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px",
        padding: "8px 10px 8px 14px",
        borderLeft: `2px solid ${borderColor}`,
        background: hover ? "#f1f5f9" : "transparent",
        borderRadius: "6px",
        transition: "background 0.12s ease",
      }}
    >
      {children}
    </div>
  );
};

// ─── main modal ───────────────────────────────────────────────────────────────

const ModalAsignarPostulantes: React.FC<IModalAsignarPostulantesProps> = ({ open, items, onClose, onAssign }) => {
  const [perItem, setPerItem] = useState<Record<string, AsignacionConfig>>({});
  const [perGroup, setPerGroup] = useState<Record<string, AsignacionConfig>>({});
  const [applyAll, setApplyAll] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<BusyScope>(null);
  const [summary, setSummary] = useState<AsignacionSummary | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset transient UI state cada vez que se abra el modal
  useEffect(() => {
    if (open) {
      setBusy(null);
      setSummary(null);
      setSubmitError(null);
      setCrossByItem({});
      setCrossByGroup({});
      void fetchClients();
    }
  }, [open]);

  const isBusyAll = busy?.kind === "all";
  const isBusyItem = (code: string) => busy?.kind === "item" && busy.code === code;
  const isAnyBusy = busy !== null;

  // Esc cierra el modal, salvo mientras se está asignando.
  useEscapeKey(() => {
    if (!isAnyBusy) onClose();
  }, open);

  // Caches keyed by parent value
  const [businessAreasByClient, setBusinessAreasByClient] = useState<Record<string, IBusinessAreaOption[]>>({});
  const [vacanciesByArea, setVacanciesByArea] = useState<Record<string, IVacancyOption[]>>({});
  const [recruitersByVacancy, setRecruitersByVacancy] = useState<Record<string, IRecruiterOption[]>>({});

  // ── reasignación a otro cliente ──────────────────────────────────────────────
  // Clientes a los que se puede asignar (con áreas/procesos activos) y bloqueos
  // de cada persona, para reasignar postulaciones a un cliente distinto al
  // original — obligatorio para las inhabilitadas por cliente/proceso, opcional
  // (vía toggle) para las disponibles.
  const [clients, setClients] = useState<IClientOption[]>([]);
  const [blocksByPerson, setBlocksByPerson] = useState<Record<string, IPersonBlocks | "loading">>({});
  // code -> el usuario activó "asignar a otro cliente" para ese registro.
  const [crossByItem, setCrossByItem] = useState<Record<string, boolean>>({});
  // groupKey -> reasignación masiva del grupo a otro cliente (con "Aplicar a todos").
  const [crossByGroup, setCrossByGroup] = useState<Record<string, boolean>>({});

  // Un registro se reasigna individualmente a otro cliente si está inhabilitado
  // para el suyo (obligatorio) o si el usuario activó su toggle.
  const isRowIndividualCross = (item: AsignacionItem): boolean =>
    isInhabItem(item) || !!crossByItem[item.code];

  // ¿La fila termina asignándose a otro cliente? Individual, o porque su grupo
  // está en modo "otro cliente" con "Aplicar a todos" (reasignación masiva).
  const rowCross = (item: AsignacionItem, groupKey?: string): boolean =>
    isRowIndividualCross(item) ||
    (!!groupKey && !!crossByGroup[groupKey] && !!applyAll[groupKey]);

  // ¿La fila la gobierna el maestro del grupo ("Aplicar a todos")?
  const followsMaster = (item: AsignacionItem, groupKey?: string): boolean =>
    !!groupKey &&
    !!applyAll[groupKey] &&
    (!!crossByGroup[groupKey] || !isRowIndividualCross(item));

  const fetchClients = async () => {
    const res = await fetchJson<{ clients: IClientOption[] }>(
      `${apiBase()}/catalogs/clients`,
    );
    setClients(res?.clients ?? []);
  };

  const fetchPersonBlocks = (personId?: string, dni?: string) => {
    if (!personId || !dni || blocksByPerson[personId]) return;
    setBlocksByPerson((prev) => ({ ...prev, [personId]: "loading" }));
    void fetchJson<IPersonBlocks>(
      `${apiBase()}/application/person-blocks?dni=${encodeURIComponent(dni)}`,
    ).then((res) => {
      setBlocksByPerson((prev) => ({
        ...prev,
        [personId]: res ?? { globalBlock: false, blockedClientIds: [], blockedVacancyIds: [] },
      }));
    });
  };

  // Mapa personId → item para poder enriquecer el resumen con el formType
  const itemByPersonId = useMemo(() => {
    const map: Record<string, AsignacionItem> = {};
    for (const it of items) {
      if (it.personId) map[it.personId] = it;
    }
    return map;
  }, [items]);

  // Agrupa los resultados del summary por tipo de formulario (CLIENTE/CUENTA/RECLUTADOR)
  const summaryByType = useMemo(() => {
    if (!summary) return null;
    const out: Record<AsignacionFormType | "OTROS", AsignacionResultItem[]> = {
      CLIENTE: [], CUENTA: [], RECLUTADOR: [], OTROS: [],
    };
    for (const result of summary.results) {
      const item = itemByPersonId[result.personId];
      const tipo = (item?.formType || "").toUpperCase() as AsignacionFormType;
      if (tipo === "CLIENTE" || tipo === "CUENTA" || tipo === "RECLUTADOR") {
        out[tipo].push(result);
      } else {
        out.OTROS.push(result);
      }
    }
    return out;
  }, [summary, itemByPersonId]);

  // Segrega items por tipo de formulario
  const byType = useMemo(() => {
    const out: Record<AsignacionFormType, AsignacionItem[]> = { CLIENTE: [], CUENTA: [], RECLUTADOR: [] };
    for (const it of items) {
      const t = (it.formType || "").toUpperCase() as AsignacionFormType;
      if (t === "CLIENTE" || t === "CUENTA" || t === "RECLUTADOR") out[t].push(it);
    }
    return out;
  }, [items]);

  const clienteGroups = useMemo(() => groupBy(byType.CLIENTE, (i) => formatGroupKey(i.clientId || i.client)), [byType.CLIENTE]);
  const cuentaGroups = useMemo(() => groupBy(byType.CUENTA, (i) => formatGroupKey(i.clientId || i.client, i.businessArea)), [byType.CUENTA]);

  // ── fetchers ────────────────────────────────────────────────────────────────
  const fetchBusinessAreas = async (clientId: string) => {
    if (!clientId || businessAreasByClient[clientId]) return;
    const res = await fetchJson<{ businessAreas: IBusinessAreaOption[] }>(
      `${apiBase()}/catalogs/business-areas?clientId=${encodeURIComponent(clientId)}`
    );
    setBusinessAreasByClient((prev) => ({ ...prev, [clientId]: res?.businessAreas ?? [] }));
  };

  const fetchVacancies = async (businessAreaCode: string) => {
    if (!businessAreaCode || vacanciesByArea[businessAreaCode]) return;
    const res = await fetchJson<{ vacancies: IVacancyOption[] }>(
      `${apiBase()}/catalogs/vacancies?businessAreaCode=${encodeURIComponent(businessAreaCode)}`
    );
    setVacanciesByArea((prev) => ({ ...prev, [businessAreaCode]: res?.vacancies ?? [] }));
  };

  const fetchRecruiters = async (vacancyId: string) => {
    if (!vacancyId || recruitersByVacancy[vacancyId]) return;
    const res = await fetchJson<{ recruiters: IRecruiterOption[] }>(
      `${apiBase()}/catalogs/recruiters?vacancyId=${encodeURIComponent(vacancyId)}`
    );
    setRecruitersByVacancy((prev) => ({ ...prev, [vacancyId]: res?.recruiters ?? [] }));
  };

  // Carga inicial: areas por cliente (CLIENTE) y vacantes por area (CUENTA).
  useEffect(() => {
    if (!open) return;
    const clientIdsCliente = new Set<string>();
    byType.CLIENTE.forEach((it) => { if (it.clientId) clientIdsCliente.add(it.clientId); });
    clientIdsCliente.forEach((c) => { void fetchBusinessAreas(c); });

    const areasCuenta = new Set<string>();
    byType.CUENTA.forEach((it) => { if (it.businessArea) areasCuenta.add(it.businessArea); });
    areasCuenta.forEach((a) => { void fetchVacancies(a); });
  }, [open, items]);

  // Los registros que van a otro cliente de forma individual (inhabilitados o con
  // su toggle activo) necesitan los bloqueos de la persona para ocultar/advertir
  // clientes. En reasignación masiva de grupo no se filtra por persona: el
  // backend revalida cada una y devuelve los rechazos.
  useEffect(() => {
    if (!open) return;
    items.forEach((it) => {
      if (isRowIndividualCross(it)) fetchPersonBlocks(it.personId, it.DNI);
    });
    // Deps intencionales: fetchPersonBlocks/isRowIndividualCross son estables y no
    // se listan a proposito. No se usa el disable de react-hooks/exhaustive-deps
    // porque la config de CRA (eslint-config-react-app/base) no carga ese plugin y
    // referenciar la regla rompe el build ("Definition for rule ... was not found").
  }, [open, items, crossByItem]);

  // ── helpers para opciones de selects ───────────────────────────────────────
  const areaOptions = (clientId?: string): { code: string; name: string }[] => {
    const list = clientId ? businessAreasByClient[clientId] : undefined;
    return (list ?? []).map((a) => ({ code: a.code, name: a.code }));
  };

  const vacancyOptions = (businessAreaCode?: string): { code: string; name: string }[] => {
    const list = businessAreaCode ? vacanciesByArea[businessAreaCode] : undefined;
    return (list ?? []).map((v) => ({ code: v.vacancyId, name: v.jobName ? `${v.vacancyId} - ${v.jobName}` : v.vacancyId }));
  };

  const recruiterOptions = (vacancyId?: string): { code: string; name: string }[] => {
    const list = vacancyId ? recruitersByVacancy[vacancyId] : undefined;
    return (list ?? []).map((r) => ({ code: r.userId, name: r.name }));
  };

  // ── updaters con cascada ────────────────────────────────────────────────────
  const updateItemConfig = (code: string, patch: Partial<AsignacionConfig>) => {
    setPerItem((prev) => {
      const current = prev[code] ?? {};
      const next: AsignacionConfig = { ...current, ...patch };
      // Cambiar el cliente de destino invalida área, proceso y reclutador.
      if ("targetClientId" in patch) {
        next.businessAreaCode = undefined;
        next.vacancyId = undefined;
        next.recruiterId = undefined;
      } else if ("businessAreaCode" in patch) { next.vacancyId = undefined; next.recruiterId = undefined; }
      else if ("vacancyId" in patch) { next.recruiterId = undefined; }
      return { ...prev, [code]: next };
    });
    if (patch.targetClientId) void fetchBusinessAreas(patch.targetClientId);
    if (patch.businessAreaCode) void fetchVacancies(patch.businessAreaCode);
    if (patch.vacancyId) void fetchRecruiters(patch.vacancyId);
  };

  // Opciones de cliente destino: los clientes asignables, menos el cliente
  // original y menos aquellos en los que la persona está inhabilitada.
  const clientOptionsForItem = (item: AsignacionItem): { code: string; name: string }[] => {
    const blocks = item.personId ? blocksByPerson[item.personId] : undefined;
    const blockedSet = blocks && blocks !== "loading"
      ? new Set(blocks.blockedClientIds)
      : new Set<string>();
    return clients
      .filter((c) => c.clientId !== item.clientId && !blockedSet.has(c.clientId))
      .map((c) => ({ code: c.clientId, name: c.name }));
  };

  const personGlobalBlock = (item: AsignacionItem): boolean => {
    const blocks = item.personId ? blocksByPerson[item.personId] : undefined;
    return blocks !== undefined && blocks !== "loading" && blocks.globalBlock;
  };

  const toggleCross = (item: AsignacionItem) => {
    setCrossByItem((prev) => ({ ...prev, [item.code]: !prev[item.code] }));
    // Al desactivar el cross, se limpia el cliente destino elegido.
    if (crossByItem[item.code]) {
      updateItemConfig(item.code, { targetClientId: undefined, targetClientName: undefined });
    } else {
      fetchPersonBlocks(item.personId, item.DNI);
    }
  };

  // Opciones de cliente destino para reasignación masiva del grupo: todos los
  // clientes asignables menos el original del grupo (los bloqueos por persona los
  // revalida el backend por registro).
  const clientOptionsForGroup = (members: AsignacionItem[]): { code: string; name: string }[] => {
    const originId = members[0]?.clientId;
    return clients
      .filter((c) => c.clientId !== originId)
      .map((c) => ({ code: c.clientId, name: c.name }));
  };

  const toggleGroupCross = (groupKey: string) => {
    const nextActive = !crossByGroup[groupKey];
    setCrossByGroup((prev) => ({ ...prev, [groupKey]: nextActive }));
    // Al cambiar de modo se limpia la config maestra (área/proceso/reclutador y
    // cliente destino ya no aplican).
    setPerGroup((prev) => ({ ...prev, [groupKey]: {} }));
    // La reasignación masiva a otro cliente opera con "Aplicar a todos": se
    // enciende al activar el modo y se apaga al salir, para un estado coherente.
    setApplyAll((prev) => ({ ...prev, [groupKey]: nextActive }));
  };

  const updateGroupConfig = (groupKey: string, patch: Partial<AsignacionConfig>) => {
    setPerGroup((prev) => {
      const current = prev[groupKey] ?? {};
      const next: AsignacionConfig = { ...current, ...patch };
      if ("targetClientId" in patch) {
        next.businessAreaCode = undefined;
        next.vacancyId = undefined;
        next.recruiterId = undefined;
      } else if ("businessAreaCode" in patch) { next.vacancyId = undefined; next.recruiterId = undefined; }
      else if ("vacancyId" in patch) { next.recruiterId = undefined; }
      return { ...prev, [groupKey]: next };
    });
    if (patch.targetClientId) void fetchBusinessAreas(patch.targetClientId);
    if (patch.businessAreaCode) void fetchVacancies(patch.businessAreaCode);
    if (patch.vacancyId) void fetchRecruiters(patch.vacancyId);
  };

  const toggleApplyAll = (groupKey: string, members: AsignacionItem[]) => {
    setApplyAll((prev) => {
      const nextActive = !prev[groupKey];
      if (nextActive) {
        const cfg = perGroup[groupKey];
        const gCross = !!crossByGroup[groupKey];
        if (cfg) {
          setPerItem((p) => {
            const next = { ...p };
            for (const m of members) {
              // En modo grupo→otro cliente se aplica a todos; en modo cliente
              // original, solo a los que no van individualmente a otro cliente.
              if (!gCross && isRowIndividualCross(m)) continue;
              next[m.code] = { ...next[m.code], ...cfg };
            }
            return next;
          });
        }
      }
      return { ...prev, [groupKey]: nextActive };
    });
  };

  // Cuando "Aplicar a todos" está activo y cambia la config maestra, propaga
  useEffect(() => {
    Object.keys(applyAll).forEach((groupKey) => {
      if (!applyAll[groupKey]) return;
      const cfg = perGroup[groupKey];
      if (!cfg) return;
      const members = clienteGroups[groupKey] ?? cuentaGroups[groupKey] ?? [];
      const gCross = !!crossByGroup[groupKey];
      setPerItem((p) => {
        const next = { ...p };
        for (const m of members) {
          if (!gCross && isRowIndividualCross(m)) continue;
          next[m.code] = { ...next[m.code], ...cfg };
        }
        return next;
      });
    });
  }, [perGroup, applyAll, crossByGroup]);

  const effectiveConfig = (item: AsignacionItem, groupKey: string): AsignacionConfig => {
    // Si la fila la gobierna el maestro del grupo, usa la config del grupo; si no,
    // su propia config individual.
    if (followsMaster(item, groupKey) && perGroup[groupKey]) return perGroup[groupKey];
    return perItem[item.code] ?? {};
  };

  // ¿La fila cross tiene todo lo necesario para asignar? (destino sin bloqueo
  // global, cliente destino, proceso y reclutador elegidos).
  const crossReady = (item: AsignacionItem, cfg: AsignacionConfig): boolean =>
    !personGlobalBlock(item) && !!cfg.targetClientId && !!cfg.vacancyId && !!cfg.recruiterId;

  // Cascada de reasignación a otro cliente: Cliente destino → Área → Proceso →
  // Reclutador. Se usa en las tres secciones (CLIENTE/CUENTA/RECLUTADOR).
  const crossRowControls = (
    item: AsignacionItem,
    cfg: AsignacionConfig,
    clientOpts: { code: string; name: string }[],
    disabled: boolean,
  ): JSX.Element => (
    <>
      <MiniSelect value={cfg.targetClientId} onChange={(v) => updateItemConfig(item.code, { targetClientId: v, targetClientName: clients.find((c) => c.clientId === v)?.name })} options={clientOpts} placeholder="Cliente destino" width={148} disabled={disabled} />
      <MiniSelect value={cfg.businessAreaCode} onChange={(v) => updateItemConfig(item.code, { businessAreaCode: v })} options={areaOptions(cfg.targetClientId)} placeholder="Área negocio" width={116} disabled={disabled || !cfg.targetClientId} />
      <MiniSelect value={cfg.vacancyId} onChange={(v) => updateItemConfig(item.code, { vacancyId: v })} options={vacancyOptions(cfg.businessAreaCode)} placeholder="Proceso" width={116} disabled={disabled || !cfg.businessAreaCode} />
      <MiniSelect value={cfg.recruiterId} onChange={(v) => updateItemConfig(item.code, { recruiterId: v })} options={recruiterOptions(cfg.vacancyId)} placeholder="Reclutador" width={126} disabled={disabled || !cfg.vacancyId} />
    </>
  );

  // Bloque de identificación del postulante + origen/destino + toggle "otro
  // cliente" (o chip obligatorio) + advertencias de inhabilitación.
  const renderRowInfo = (
    item: AsignacionItem,
    cfg: AsignacionConfig,
    cross: boolean,
    driven: boolean,
  ): JSX.Element => {
    const inhab = isInhabItem(item);
    const globalBlk = personGlobalBlock(item);
    const noClients = cross && !driven && !globalBlk && clientOptionsForItem(item).length === 0;
    return (
      <div style={{ flex: 1, minWidth: "200px" }}>
        <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "#1e293b", fontWeight: 500 }}>{item.names || "—"}</p>
        <p style={{ margin: 0, fontFamily: "ui-monospace, monospace", fontSize: "11px", color: "#64748b" }}>{item.DNI || "—"} · {item.code}</p>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "5px", flexWrap: "wrap" }}>
          <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#64748b" }}>
            Postuló a: <strong style={{ color: "#475569" }}>{item.client || "—"}</strong>
            {cross && cfg.targetClientName && (
              <> → <strong style={{ color: "#b45309" }}>{cfg.targetClientName}</strong></>
            )}
          </span>
          {driven ? (
            cross && (
              <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "10px", fontWeight: 700, color: "#b45309", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Reasignación por grupo
              </span>
            )
          ) : inhab ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "999px", background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa", fontFamily: "Roboto, sans-serif", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Reasignar a otro cliente
            </span>
          ) : (
            <CrossClientToggle active={cross} onToggle={() => { if (!isAnyBusy) toggleCross(item); }} />
          )}
        </div>
        {cross && !driven && globalBlk && (
          <p style={{ margin: "6px 0 0", fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#b91c1c", fontWeight: 600 }}>
            Inhabilitación global: esta persona no puede asignarse a ningún cliente.
          </p>
        )}
        {noClients && (
          <p style={{ margin: "6px 0 0", fontFamily: "Roboto, sans-serif", fontSize: "11px", color: "#b45309", fontWeight: 600 }}>
            No hay clientes sin inhabilitación disponibles para esta persona.
          </p>
        )}
      </div>
    );
  };

  const runAssign = async (
    payload: { item: AsignacionItem; config: AsignacionConfig }[],
    scope: BusyScope,
  ) => {
    if (!onAssign || isAnyBusy) return;
    setBusy(scope);
    setSubmitError(null);
    try {
      const result = await onAssign(payload);
      if (result) setSummary(result);
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Ocurrió un error inesperado al asignar.",
      );
    } finally {
      setBusy(null);
    }
  };

  const assignOne = async (item: AsignacionItem, groupKey?: string) => {
    const cfg = groupKey ? effectiveConfig(item, groupKey) : (perItem[item.code] ?? {});
    await runAssign([{ item, config: cfg }], { kind: "item", code: item.code });
  };

  const assignAll = async () => {
    const payload = items.map((item) => {
      const t = (item.formType || "").toUpperCase() as AsignacionFormType;
      let groupKey = "";
      if (t === "CLIENTE") groupKey = formatGroupKey(item.clientId || item.client);
      else if (t === "CUENTA") groupKey = formatGroupKey(item.clientId || item.client, item.businessArea);
      return { item, config: effectiveConfig(item, groupKey) };
    });
    await runAssign(payload, { kind: "all" });
  };

  // Controles maestros del grupo cuando se reasigna a otro cliente (bulk).
  const crossGroupMaster = (groupKey: string, members: AsignacionItem[]): JSX.Element => {
    const masterCfg = perGroup[groupKey] ?? {};
    const gDisabled = !applyAll[groupKey] || isBusyAll;
    return (
      <>
        <MiniSelect value={masterCfg.targetClientId} onChange={(v) => updateGroupConfig(groupKey, { targetClientId: v, targetClientName: clients.find((c) => c.clientId === v)?.name })} options={clientOptionsForGroup(members)} placeholder="Cliente destino" disabled={gDisabled} />
        <MiniSelect value={masterCfg.businessAreaCode} onChange={(v) => updateGroupConfig(groupKey, { businessAreaCode: v })} options={areaOptions(masterCfg.targetClientId)} placeholder="Área negocio" disabled={gDisabled || !masterCfg.targetClientId} />
        <MiniSelect value={masterCfg.vacancyId} onChange={(v) => updateGroupConfig(groupKey, { vacancyId: v })} options={vacancyOptions(masterCfg.businessAreaCode)} placeholder="Proceso" disabled={gDisabled || !masterCfg.businessAreaCode} />
        <MiniSelect value={masterCfg.recruiterId} onChange={(v) => updateGroupConfig(groupKey, { recruiterId: v })} options={recruiterOptions(masterCfg.vacancyId)} placeholder="Reclutador" disabled={gDisabled || !masterCfg.vacancyId} />
      </>
    );
  };

  // Fila individual (CLIENTE/CUENTA): identificación + controles normales o de
  // reasignación a otro cliente + botón Asignar. `ownControls` dibuja los selects
  // del cliente original propios de cada sección.
  const renderMemberRow = (
    it: AsignacionItem,
    groupKey: string,
    accent: { active: string; idle: string },
    ownControls: (item: AsignacionItem, cfg: AsignacionConfig, disabled: boolean) => JSX.Element,
  ): JSX.Element => {
    const cfg = effectiveConfig(it, groupKey);
    const driven = followsMaster(it, groupKey);
    const cross = rowCross(it, groupKey);
    const globalBlk = personGlobalBlock(it);
    const selectsDisabled = driven || isAnyBusy;
    const clientOpts = driven
      ? clients.map((c) => ({ code: c.clientId, name: c.name }))
      : (globalBlk ? [] : clientOptionsForItem(it));
    const assignDisabled = driven
      || (isAnyBusy && !isBusyItem(it.code))
      || (cross ? !crossReady(it, cfg) : false);
    return (
      <HoverableRow key={it.code} borderColor={cross ? "#f59e0b" : (driven ? accent.active : accent.idle)}>
        {renderRowInfo(it, cfg, cross, driven)}
        {cross ? crossRowControls(it, cfg, clientOpts, selectsDisabled) : ownControls(it, cfg, selectsDisabled)}
        <div style={{ marginLeft: "auto" }}>
          <RowActionButton
            label="Asignar"
            onClick={() => { void assignOne(it, groupKey); }}
            loading={isBusyItem(it.code)}
            disabled={assignDisabled}
          />
        </div>
      </HoverableRow>
    );
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 1000, padding: "20px",
        }}
      >
        <motion.div
          key="dialog"
          initial={{ opacity: 0, scale: 0.96, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 8 }}
          transition={{ type: "spring", stiffness: 320, damping: 28 }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff", borderRadius: "16px",
            width: "min(96vw, 980px)", maxHeight: "90vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(15,23,42,0.35)",
            overflow: "hidden",
          }}
        >
          <style>{`
            .asignar-btn { position: relative; overflow: hidden; isolation: isolate; }
            .asignar-btn::after {
              content: ""; position: absolute; top: 0; left: 0;
              width: 40%; height: 100%;
              background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%);
              transform: translateX(-120%) skewX(-20deg);
              transition: transform 0.4s ease-in-out;
              pointer-events: none; z-index: 1;
            }
            .asignar-btn:hover::after { transform: translateX(260%) skewX(-20deg); }
            .asignar-btn > * { position: relative; z-index: 2; }
            @keyframes asignar-spin { to { transform: rotate(360deg); } }
          `}</style>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 18px", borderBottom: "1px solid #e2e8f0", background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)" }}>
            <GroupAddIcon style={{ fontSize: 22, color: "#38bdf8" }} />
            <div style={{ flex: 1 }}>
              <h2 style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "16px", fontWeight: 700, color: "#fff" }}>
                Asignar postulantes
              </h2>
              <p style={{ margin: "2px 0 0 0", fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "rgba(255,255,255,0.65)" }}>
                {items.length} {items.length === 1 ? "registro" : "registros"} agrupados por tipo de formulario
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Cerrar"
              style={{ display: "flex", padding: "6px", borderRadius: "8px", border: "none", background: "rgba(255,255,255,0.08)", color: "#fff", cursor: "pointer" }}
            >
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: "auto", padding: "16px 18px", background: "#f8fafc" }}>
            {items.length === 0 && !summary && (
              <p style={{ textAlign: "center", color: "#94a3b8", fontFamily: "Roboto, sans-serif", fontSize: "13px", padding: "40px 0" }}>
                No hay postulantes seleccionados.
              </p>
            )}

            {/* ── Sección CLIENTE ───────────────────────────────────── */}
            {!summary && byType.CLIENTE.length > 0 && (
              <div style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid #ddd6fe", borderTop: "4px solid #7c3aed", marginBottom: "18px", boxShadow: "0 2px 12px rgba(124,58,237,0.08)" }}>
                <SectionHeader label="Cliente" title="Asignación tipo Cliente" subtitle="Selecciona área de negocio, proceso y reclutador para cada grupo." count={byType.CLIENTE.length} color="#7c3aed" />
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "12px" }}>
                  {Object.entries(clienteGroups).map(([groupKey, members]) => {
                    const masterCfg = perGroup[groupKey] ?? {};
                    const isApplyAll = !!applyAll[groupKey];
                    const isGroupCross = !!crossByGroup[groupKey];
                    const clientId = members[0]?.clientId;
                    return (
                      <div key={groupKey} style={{ borderRadius: "10px", border: `2px solid ${isApplyAll ? "#7c3aed" : "#ddd6fe"}`, overflow: "hidden", background: "#fff", boxShadow: "0 2px 8px rgba(124,58,237,0.10)" }}>
                        {/* Grupo: asignación masiva */}
                        <div style={{ background: "linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)", borderBottom: "2px dashed #ddd6fe" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px 0" }}>
                            <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#7c3aed", fontFamily: "Roboto, sans-serif" }}>
                              ⬡ Asignación grupal
                            </span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "8px 14px 8px" }}>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 700, color: "#4c1d95", minWidth: "150px", flex: 1 }}>
                              {members[0]?.client || "—"}
                              {isGroupCross && (
                                <span style={{ marginLeft: "6px", color: "#b45309", fontSize: "11px", fontWeight: 700 }}>→ otro cliente</span>
                              )}
                            </span>
                            {isGroupCross ? crossGroupMaster(groupKey, members) : (
                              <>
                                <MiniSelect value={masterCfg.businessAreaCode} onChange={(v) => updateGroupConfig(groupKey, { businessAreaCode: v })} options={areaOptions(clientId)} placeholder="Área negocio" disabled={!isApplyAll || isBusyAll} />
                                <MiniSelect value={masterCfg.vacancyId} onChange={(v) => updateGroupConfig(groupKey, { vacancyId: v })} options={vacancyOptions(masterCfg.businessAreaCode)} placeholder="Proceso" disabled={!isApplyAll || isBusyAll || !masterCfg.businessAreaCode} />
                                <MiniSelect value={masterCfg.recruiterId} onChange={(v) => updateGroupConfig(groupKey, { recruiterId: v })} options={recruiterOptions(masterCfg.vacancyId)} placeholder="Reclutador" disabled={!isApplyAll || isBusyAll || !masterCfg.vacancyId} />
                              </>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "0 14px 12px" }}>
                            <ApplyAllToggle active={isApplyAll} onToggle={() => { if (!isBusyAll) toggleApplyAll(groupKey, members); }} />
                            <div style={{ marginLeft: "auto" }}>
                              <CrossClientToggle active={isGroupCross} onToggle={() => { if (!isBusyAll) toggleGroupCross(groupKey); }} />
                            </div>
                          </div>
                        </div>
                        {/* Registros individuales */}
                        <div style={{ padding: "8px 10px", background: "#faf9ff" }}>
                          <SubSeparator label="Registros individuales" color="#7c3aed" />
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                            {members.map((it) => renderMemberRow(it, groupKey, { active: "#7c3aed", idle: "#ddd6fe" }, (item, cfg, disabled) => (
                              <>
                                <MiniSelect value={cfg.businessAreaCode} onChange={(v) => updateItemConfig(item.code, { businessAreaCode: v })} options={areaOptions(item.clientId)} placeholder="Área negocio" width={140} disabled={disabled} />
                                <MiniSelect value={cfg.vacancyId} onChange={(v) => updateItemConfig(item.code, { vacancyId: v })} options={vacancyOptions(cfg.businessAreaCode)} placeholder="Proceso" width={140} disabled={disabled || !cfg.businessAreaCode} />
                                <MiniSelect value={cfg.recruiterId} onChange={(v) => updateItemConfig(item.code, { recruiterId: v })} options={recruiterOptions(cfg.vacancyId)} placeholder="Reclutador" width={150} disabled={disabled || !cfg.vacancyId} />
                              </>
                            )))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Sección CUENTA ────────────────────────────────────── */}
            {!summary && byType.CUENTA.length > 0 && (
              <div style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid #bae6fd", borderTop: "4px solid #0ea5e9", marginBottom: "18px", boxShadow: "0 2px 12px rgba(14,165,233,0.08)" }}>
                <SectionHeader label="Cuenta" title="Asignación tipo Cuenta" subtitle="Selecciona proceso y reclutador para cada área de negocio." count={byType.CUENTA.length} color="#0ea5e9" />
                <div style={{ display: "flex", flexDirection: "column", gap: "10px", padding: "12px" }}>
                  {Object.entries(cuentaGroups).map(([groupKey, members]) => {
                    const masterCfg = perGroup[groupKey] ?? {};
                    const isApplyAll = !!applyAll[groupKey];
                    const isGroupCross = !!crossByGroup[groupKey];
                    const businessAreaCode = members[0]?.businessArea;
                    return (
                      <div key={groupKey} style={{ borderRadius: "10px", border: `2px solid ${isApplyAll ? "#0ea5e9" : "#bae6fd"}`, overflow: "hidden", background: "#fff", boxShadow: "0 2px 8px rgba(14,165,233,0.10)" }}>
                        {/* Grupo: asignación masiva */}
                        <div style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)", borderBottom: "2px dashed #bae6fd" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px 0" }}>
                            <span style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "#0284c7", fontFamily: "Roboto, sans-serif" }}>
                              ⬡ Asignación grupal
                            </span>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", padding: "8px 14px 8px" }}>
                            <div style={{ flex: 1, minWidth: "180px" }}>
                              <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 700, color: "#0c4a6e" }}>
                                {members[0]?.client || "—"}
                                {isGroupCross && (
                                  <span style={{ marginLeft: "6px", color: "#b45309", fontSize: "11px", fontWeight: 700 }}>→ otro cliente</span>
                                )}
                              </p>
                              <p style={{ margin: 0, fontFamily: "ui-monospace, monospace", fontSize: "11px", color: "#0369a1" }}>Área: {members[0]?.businessArea || "—"}</p>
                            </div>
                            {isGroupCross ? crossGroupMaster(groupKey, members) : (
                              <>
                                <MiniSelect value={masterCfg.vacancyId} onChange={(v) => updateGroupConfig(groupKey, { vacancyId: v })} options={vacancyOptions(businessAreaCode)} placeholder="Proceso" disabled={!isApplyAll || isBusyAll} />
                                <MiniSelect value={masterCfg.recruiterId} onChange={(v) => updateGroupConfig(groupKey, { recruiterId: v })} options={recruiterOptions(masterCfg.vacancyId)} placeholder="Reclutador" disabled={!isApplyAll || isBusyAll || !masterCfg.vacancyId} />
                              </>
                            )}
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "0 14px 12px" }}>
                            <ApplyAllToggle active={isApplyAll} onToggle={() => { if (!isBusyAll) toggleApplyAll(groupKey, members); }} />
                            <div style={{ marginLeft: "auto" }}>
                              <CrossClientToggle active={isGroupCross} onToggle={() => { if (!isBusyAll) toggleGroupCross(groupKey); }} />
                            </div>
                          </div>
                        </div>
                        {/* Registros individuales */}
                        <div style={{ padding: "8px 10px", background: "#f7fbff" }}>
                          <SubSeparator label="Registros individuales" color="#0ea5e9" />
                          <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "4px" }}>
                            {members.map((it) => renderMemberRow(it, groupKey, { active: "#0ea5e9", idle: "#bae6fd" }, (item, cfg, disabled) => (
                              <>
                                <MiniSelect value={cfg.vacancyId} onChange={(v) => updateItemConfig(item.code, { vacancyId: v })} options={vacancyOptions(item.businessArea)} placeholder="Proceso" width={140} disabled={disabled} />
                                <MiniSelect value={cfg.recruiterId} onChange={(v) => updateItemConfig(item.code, { recruiterId: v })} options={recruiterOptions(cfg.vacancyId)} placeholder="Reclutador" width={150} disabled={disabled || !cfg.vacancyId} />
                              </>
                            )))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Sección RECLUTADOR ────────────────────────────────── */}
            {!summary && byType.RECLUTADOR.length > 0 && (
              <div style={{ borderRadius: "14px", overflow: "hidden", border: "1px solid #fde68a", borderTop: "4px solid #f59e0b", marginBottom: "18px", boxShadow: "0 2px 12px rgba(245,158,11,0.08)" }}>
                <SectionHeader label="Reclutador" title="Asignación tipo Reclutador" subtitle="Datos ya definidos por el formulario. Puedes confirmar la asignación o reasignar a otro cliente." count={byType.RECLUTADOR.length} color="#f59e0b" />
                <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {byType.RECLUTADOR.map((it) => {
                    const cfg = perItem[it.code] ?? {};
                    const cross = rowCross(it);
                    const globalBlk = personGlobalBlock(it);
                    const clientOpts = globalBlk ? [] : clientOptionsForItem(it);
                    const assignDisabled = (isAnyBusy && !isBusyItem(it.code)) || (cross ? !crossReady(it, cfg) : false);
                    return (
                      <HoverableRow key={it.code} borderColor={cross ? "#f59e0b" : "#fbbf24"}>
                        {renderRowInfo(it, cfg, cross, false)}
                        {cross ? crossRowControls(it, cfg, clientOpts, isAnyBusy) : (
                          <>
                            <div style={{ minWidth: "120px" }}>
                              <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "10px", fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em" }}>Proceso</p>
                              <p style={{ margin: 0, fontFamily: "ui-monospace, monospace", fontSize: "12px", color: "#1e293b" }}>{it.vacancy || "—"}</p>
                            </div>
                            <div style={{ minWidth: "140px" }}>
                              <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "10px", fontWeight: 600, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em" }}>Reclutador</p>
                              <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "12px", color: "#1e293b" }}>{it.recruiter || "—"}</p>
                            </div>
                          </>
                        )}
                        <div style={{ marginLeft: "auto" }}>
                          <RowActionButton
                            label="Asignar"
                            onClick={() => { void assignOne(it); }}
                            loading={isBusyItem(it.code)}
                            disabled={assignDisabled}
                          />
                        </div>
                      </HoverableRow>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Resumen post-procesamiento (sustituye al formulario) ────── */}
            {summary && summaryByType && (() => {
              const fullSuccess = summary.totalAssigned === summary.totalRequested;
              const TYPE_META: Record<AsignacionFormType | "OTROS", { label: string; color: string; bg: string; border: string }> = {
                CLIENTE:    { label: "Cliente",    color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
                CUENTA:     { label: "Cuenta",     color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd" },
                RECLUTADOR: { label: "Reclutador", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
                OTROS:      { label: "Otros",      color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
              };
              const orderedKeys: (AsignacionFormType | "OTROS")[] = ["CLIENTE", "CUENTA", "RECLUTADOR", "OTROS"];

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {/* Cabecera global del resumen */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: "12px",
                    padding: "12px 14px", borderRadius: "12px",
                    background: fullSuccess
                      ? "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)"
                      : "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
                    border: `1px solid ${fullSuccess ? "#a7f3d0" : "#fde68a"}`,
                  }}>
                    {fullSuccess
                      ? <CheckCircleOutlineIcon style={{ fontSize: 26, color: "#047857" }} />
                      : <ErrorOutlineIcon style={{ fontSize: 26, color: "#b45309" }} />}
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "14px", fontWeight: 700, color: fullSuccess ? "#065f46" : "#92400e" }}>
                        Resultado de la asignación
                      </p>
                      <p style={{ margin: "2px 0 0", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: fullSuccess ? "#047857" : "#b45309" }}>
                        {summary.totalAssigned} de {summary.totalRequested} postulantes asignados correctamente
                      </p>
                    </div>
                  </div>

                  {/* Tarjetas por tipo de formulario */}
                  {orderedKeys.map((key) => {
                    const list = summaryByType[key];
                    if (!list || list.length === 0) return null;
                    const meta = TYPE_META[key];
                    const assignedCount = list.filter((r) => r.assigned).length;
                    return (
                      <div key={key} style={{ borderRadius: "14px", overflow: "hidden", border: `1px solid ${meta.border}`, borderTop: `4px solid ${meta.color}`, background: "#fff", boxShadow: `0 2px 12px ${meta.color}14` }}>
                        <SectionHeader
                          label={meta.label}
                          title={`Resultado tipo ${meta.label}`}
                          subtitle={`${assignedCount} de ${list.length} asignados`}
                          count={list.length}
                          color={meta.color}
                        />
                        <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: "6px", background: meta.bg }}>
                          {list.map((result, idx) => {
                            const item = itemByPersonId[result.personId];
                            return (
                              <div
                                key={`${result.personId}-${idx}`}
                                style={{
                                  display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap",
                                  padding: "8px 12px", borderRadius: "8px",
                                  background: result.assigned ? "#ecfdf5" : "#fef2f2",
                                  border: `1px solid ${result.assigned ? "#a7f3d0" : "#fecaca"}`,
                                }}
                              >
                                {result.assigned
                                  ? <CheckCircleOutlineIcon style={{ fontSize: 18, color: "#047857", flexShrink: 0 }} />
                                  : <ErrorOutlineIcon style={{ fontSize: 18, color: "#b91c1c", flexShrink: 0 }} />}
                                <div style={{ minWidth: "180px" }}>
                                  <p style={{ margin: 0, fontFamily: "Roboto, sans-serif", fontSize: "12.5px", color: "#1e293b", fontWeight: 600 }}>
                                    {result.name ?? item?.names ?? result.personId}
                                  </p>
                                  <p style={{ margin: 0, fontFamily: "ui-monospace, monospace", fontSize: "11px", color: "#64748b" }}>
                                    {(result.DNI ?? item?.DNI ?? "—")} {item?.code ? `· ${item.code}` : ""}
                                  </p>
                                </div>
                                <div style={{ flex: 1, minWidth: "200px", display: "flex", flexDirection: "column", gap: "3px" }}>
                                  <span style={{
                                    fontFamily: "Roboto, sans-serif", fontSize: "12px",
                                    color: result.assigned ? "#047857" : "#b91c1c",
                                    fontWeight: 500,
                                  }}>
                                    {result.assigned ? "Asignado correctamente" : (result.reason ?? "No se pudo asignar")}
                                  </span>
                                  {result.crossClient && (result.originClient || result.targetClient) && (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", alignSelf: "flex-start", padding: "2px 8px", borderRadius: "999px", background: "#fff7ed", color: "#b45309", border: "1px solid #fed7aa", fontFamily: "Roboto, sans-serif", fontSize: "10.5px", fontWeight: 700 }}>
                                      Reasignado: {result.originClient || "—"} → {result.targetClient || "—"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {submitError && (
            <div style={{ padding: "10px 18px", borderTop: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", fontFamily: "Roboto, sans-serif", fontSize: "12.5px", fontWeight: 600 }}>
              {submitError}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "12px 18px", borderTop: "1px solid #e2e8f0", background: "#fff" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isAnyBusy}
              style={{
                padding: "8px 18px", borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: isAnyBusy ? "#94a3b8" : "#475569",
                cursor: isAnyBusy ? "not-allowed" : "pointer",
                fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 600,
                opacity: isAnyBusy ? 0.7 : 1,
              }}
            >
              {summary ? "Cerrar" : "Cancelar"}
            </button>
            {!summary && (() => {
              const assignAllDisabled = items.length === 0 || (isAnyBusy && !isBusyAll);
              const showSpinner = isBusyAll;
              return (
                <motion.button
                  type="button"
                  className={assignAllDisabled || showSpinner ? undefined : "asignar-btn"}
                  onClick={() => { void assignAll(); }}
                  disabled={assignAllDisabled || showSpinner}
                  whileHover={{ scale: assignAllDisabled || showSpinner ? 1 : 1.02 }}
                  whileTap={{ scale: assignAllDisabled || showSpinner ? 1 : 0.97 }}
                  style={{
                    display: "flex", alignItems: "center", gap: "6px",
                    padding: "8px 18px", borderRadius: "8px",
                    border: "1px solid #047857",
                    background: assignAllDisabled
                      ? "#d1fae5"
                      : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                    color: assignAllDisabled ? "#047857" : "#fff",
                    cursor: showSpinner ? "wait" : assignAllDisabled ? "not-allowed" : "pointer",
                    fontFamily: "Roboto, sans-serif", fontSize: "13px", fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(4,120,87,0.22)",
                    minWidth: "150px", justifyContent: "center",
                  }}
                >
                  {showSpinner
                    ? <Spinner size={14} color="#fff" />
                    : <PersonAddAlt1Icon style={{ fontSize: 16 }} />}
                  {showSpinner ? "Asignando…" : "Asignar todos"}
                </motion.button>
              );
            })()}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ModalAsignarPostulantes;
